// sound5/tension.js: the IGNITE tension cluster. The notes of music.js
// CLUSTER enter one by one as the visitor presses and holds; a low-pass
// opens, the pitch creeps up and a tremolo quickens. Each note is two
// detuned saws. A note's oscillators exist only while it is audible.
import { mtof, biquad, Knob, clamp } from './util.js';
import { Bus } from './voices.js';
import { CLUSTER } from './music.js';

export function makeCluster(eng) {
  const c = eng.ctx, cfg = eng.cfg, rng = eng.rng;
  const lp = biquad(c, 'lowpass', 400, 0.9);
  const trem = c.createGain(), lfo = c.createOscillator(), depth = c.createGain();
  trem.gain.value = 1;
  depth.gain.value = 0;
  lfo.frequency.value = 4;
  lfo.connect(depth);
  depth.connect(trem.gain);
  lfo.start();
  lp.connect(trem);
  const bus = new Bus(eng, 0.8, 0.55, [lp, trem]);
  const kLp = new Knob(lp.frequency, 400, 0.02, 2);
  const kDepth = new Knob(depth.gain, 0, 0.02, 1e-3);
  const kRate = new Knob(lfo.frequency, 4, 0.02, 0.05);
  const kTrem = new Knob(trem.gain, 1, 0.01, 1e-3);
  const notes = CLUSTER.slice(0, cfg.clusterNotes);
  const vs = notes.map(() => null);
  const AMP = 0.05;

  const spawn = (k, t, rise) => {
    const g = c.createGain();
    g.gain.value = 0;
    g.connect(bus.in);
    const oscs = [], det = [];
    for (let j = 0; j < 2; j++) {
      const o = c.createOscillator(), base = (j ? 6 : -6) + (rng() - 0.5) * 3;
      o.type = 'sawtooth';
      o.frequency.value = mtof(notes[k]);
      o.detune.value = base + rise;
      eng.bendTo(o.detune);
      o.onended = () => { eng.unbend(o.detune); try { o.disconnect(); } catch (e) { /* ignore */ } };
      o.connect(g);
      o.start(t);
      oscs.push(o);
      det.push([new Knob(o.detune, base + rise, 0, 0.5), base]);
    }
    return { g, oscs, det, kg: new Knob(g.gain, 0, 0.02, 1e-4), offAt: 0 };
  };

  bus.count = () => vs.reduce((n, v) => n + (v ? v.oscs.length : 0), 0);
  // T: scene targets (cluster level, q = charge, clusterCut, clusterRise, trem)
  bus.update = (t, T) => {
    bus.set(T.cluster, t, T.cluster > bus.target ? 0.12 : 0.3);
    kLp.set(T.clusterCut, t, 0.12);
    const d = 0.55 * T.trem;
    kDepth.set(d / 2, t, 0.2);
    kTrem.set(1 - d / 2, t, 0.2);
    kRate.set(3 + 7 * T.trem, t, 0.3);
    const n = notes.length, on = bus.target > 0.004;
    for (let k = 0; k < n; k++) {
      // note k fades in over a tenth of the hold, in CLUSTER order
      const w = on ? clamp(T.q * n * 1.15 - k * 1.0, 0, 1) : 0;
      let v = vs[k];
      if (w > 0.001) {
        if (!v) v = vs[k] = spawn(k, t, T.clusterRise);
        v.offAt = 0;
        v.kg.set(w * AMP, t, 0.18);
        for (const [kd, base] of v.det) kd.set(base + T.clusterRise, t, 0.2);
      } else if (v) {
        v.kg.set(0, t, 0.25);
        if (!v.offAt) v.offAt = t + 2;
        else if (t > v.offAt) {
          for (const o of v.oscs) try { o.stop(t + 0.05); } catch (e) { /* ignore */ }
          vs[k] = null;
        }
      }
    }
  };
  // ignition: cut the tension fast (the hit covers it)
  bus.kill = (t) => {
    bus.target = 0;
    bus.k.go(0, t, 0.05);
    for (const v of vs) if (v) { v.kg.go(0, t, 0.05); v.kg.v = 0; }
  };
  return bus;
}
